import { useMemo, useState } from "react";
import { Grid, Icon, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import { useMaterialUIController } from "context";
import {
    CLIENTES, esforcoPorCliente, formatarDecimal, formatarNumero, formatarPercentual, formatarPercentualCurto, temaGrafico,
} from "./analiseUtils";

const ORDENACOES = [
    { id: "fichas", value: "fichas", label: "Fichas acionadas" },
    { id: "atendimentos", value: "atendimentos", label: "Atendimentos" },
    { id: "nome", value: "nome", label: "Nome" },
    ...CLIENTES.map(c => ({ id: `pct-${c}`, value: `pct-${c}`, label: `% ${c}` })),
];

// Intensidade de uma cor só (a das fichas) proporcional ao %: é magnitude, não identidade de cliente
const fundoIntensidade = (percentual, darkMode) => {
    if (!percentual) return "transparent";
    const alfa = (darkMode ? 0.1 : 0.06) + (darkMode ? 0.6 : 0.64) * percentual;
    return darkMode ? `rgba(144, 133, 233, ${alfa.toFixed(3)})` : `rgba(74, 58, 167, ${alfa.toFixed(3)})`;
};
const textoIntensidade = (percentual, darkMode, padrao) => {
    if (!percentual || percentual < 0.6) return padrao;
    return darkMode ? "#0b0b0b" : "#ffffff";
};

/**
 * Matriz negociador × cliente: em cada célula, o % do esforço da pessoa naquele cliente e as fichas acionadas.
 * Cada linha soma 100%. A linha "Todos" usa os totais do backend (cada ficha uma vez).
 */
export default function MatrizEsforco({ blocos, consolidado, totaisClientes, selecionado, clienteDestaque, rotuloPessoa, onVerDetalhe }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = temaGrafico(darkMode);
    const [busca, setBusca] = useState("");
    const [ordenacao, setOrdenacao] = useState(ORDENACOES[0]);

    const linhaTodos = useMemo(() => {
        const esforco = esforcoPorCliente(consolidado).map(e => {
            const total = (totaisClientes || []).find(t => t.cliente === e.cliente);
            return { ...e, fichas: total ? total.fichasPeriodo : 0, atendimentos: total ? total.atendimentosPeriodo : 0 };
        });
        const fichas = esforco.reduce((acc, e) => acc + e.fichas, 0);
        const atendimentos = esforco.reduce((acc, e) => acc + e.atendimentos, 0);
        return { nome: consolidado.nome, esforco, fichas, atendimentos, todos: true };
    }, [consolidado, totaisClientes]);

    const linhas = useMemo(() => {
        const termo = busca.trim().toUpperCase();
        const lista = blocos
            .filter(b => !selecionado || b.nome === selecionado)
            .filter(b => !termo || b.nome.toUpperCase().includes(termo))
            .map(b => ({ nome: b.nome, esforco: esforcoPorCliente(b), fichas: b.fichasPeriodo || 0, atendimentos: b.atendimentosTotal || 0 }));
        const chave = ordenacao.value;
        const valor = (l) => {
            if (chave.startsWith("pct-")) return l.esforco.find(e => e.cliente === chave.slice(4)).percentual;
            return l[chave] || 0;
        };
        return lista.sort((a, b) => (chave === "nome" ? a.nome.localeCompare(b.nome) : valor(b) - valor(a)));
    }, [blocos, selecionado, busca, ordenacao]);

    const celula = {
        display: "table-cell",
        py: 0.75,
        px: 1.25,
        fontSize: "0.8rem",
        color: `${tema.texto} !important`,
        borderBottom: `1px solid ${tema.borda}`,
        verticalAlign: "top",
        whiteSpace: "nowrap",
    };
    const cabecalho = { ...celula, fontWeight: "bold", color: `${tema.textoSecundario} !important`, fontSize: "0.75rem" };
    // Cliente filtrado: coluna destacada com a cor de ação do tema (clientes não têm cor própria)
    const corDestaque = darkMode ? "#ffffff" : "#1A73E8";

    const renderLinha = (linha) => {
        const fundoLinha = linha.todos ? (darkMode ? "rgba(255, 255, 255, 0.06)" : "#f0f2f5") : "transparent";
        return (
            <TableRow key={linha.nome} sx={{ display: "table-row", background: fundoLinha }}>
                <TableCell sx={{ ...celula, fontWeight: "bold" }}>{linha.nome}</TableCell>
                {linha.esforco.map(e => {
                    const destaque = clienteDestaque === e.cliente;
                    return (
                        <TableCell
                            key={e.cliente}
                            align="center"
                            sx={{
                                ...celula,
                                background: fundoIntensidade(e.percentual, darkMode),
                                color: `${textoIntensidade(e.percentual, darkMode, tema.texto)} !important`,
                                borderLeft: destaque ? `2px solid ${corDestaque}` : undefined,
                                borderRight: destaque ? `2px solid ${corDestaque}` : undefined,
                            }}
                        >
                            <Tooltip
                                title={e.percentual > 0
                                    ? `${e.cliente}: ${formatarPercentual(e.percentual)} do esforço · ${formatarNumero(e.fichas)} fichas · ${formatarNumero(e.atendimentos)} atendimentos`
                                    : `${e.cliente}: sem acionamento no período`}
                                placement="top"
                            >
                                <MDBox component="span" sx={{ display: "block" }}>
                                    <MDBox component="span" sx={{ display: "block", fontWeight: 700, fontSize: "0.9rem" }}>
                                        {e.percentual > 0 ? formatarPercentualCurto(e.percentual) : "–"}
                                    </MDBox>
                                    <MDBox component="span" sx={{ display: "block", fontSize: "0.7rem", opacity: 0.85 }}>
                                        {e.fichas > 0 ? `${formatarNumero(e.fichas)} fichas` : " "}
                                    </MDBox>
                                </MDBox>
                            </Tooltip>
                        </TableCell>
                    );
                })}
                <TableCell sx={{ ...celula, fontWeight: "bold" }} align="right">{formatarNumero(linha.fichas)}</TableCell>
                <TableCell sx={celula} align="right">{formatarNumero(linha.atendimentos)}</TableCell>
                <TableCell sx={celula} align="right">{formatarDecimal(linha.fichas > 0 ? linha.atendimentos / linha.fichas : null)}</TableCell>
                <TableCell sx={celula} align="center">
                    {!linha.todos && (
                        <Tooltip title="Ver % de tempo mês a mês">
                            <IconButton size="small" onClick={() => onVerDetalhe(linha.nome)} aria-label={`Ver detalhe de ${linha.nome}`}>
                                <Icon sx={{ color: darkMode ? "#fff" : "#1A73E8" }}>visibility</Icon>
                            </IconButton>
                        </Tooltip>
                    )}
                </TableCell>
            </TableRow>
        );
    };

    return (
        <MDBox>
            <Grid container spacing={2} alignItems="flex-start" mb={1.5}>
                <Grid item xs={12} sm={6} md={4}>
                    <MDInput fullWidth size="small" label={`Buscar ${rotuloPessoa.toLowerCase()}`} value={busca} onChange={e => setBusca(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MDSelect isMulti={false} size="small" name="ordenacao" value={ordenacao} options={ORDENACOES} onChange={val => setOrdenacao(val || ORDENACOES[0])} />
                </Grid>
            </Grid>

            <TableContainer sx={{ boxShadow: "none", borderRadius: 0, overflowX: "auto", background: "transparent" }}>
                <Table size="small" sx={{ width: "100%", minWidth: "1000px", borderCollapse: "collapse" }}>
                    <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow sx={{ display: "table-row" }}>
                            <TableCell sx={cabecalho}>{rotuloPessoa}</TableCell>
                            {CLIENTES.map(c => (
                                <TableCell
                                    key={c}
                                    align="center"
                                    sx={{
                                        ...cabecalho,
                                        borderBottom: clienteDestaque === c ? `3px solid ${corDestaque}` : cabecalho.borderBottom,
                                        color: `${clienteDestaque === c ? tema.texto : tema.textoSecundario} !important`,
                                    }}
                                >
                                    {c}
                                </TableCell>
                            ))}
                            <TableCell sx={cabecalho} align="right">Fichas acionadas</TableCell>
                            <TableCell sx={cabecalho} align="right">Atendimentos</TableCell>
                            <TableCell sx={cabecalho} align="right">Média por ficha</TableCell>
                            <TableCell sx={cabecalho} align="center">Mês a mês</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ display: "table-row-group" }}>
                        {renderLinha(linhaTodos)}
                        {linhas.map(renderLinha)}
                    </TableBody>
                </Table>
            </TableContainer>
        </MDBox>
    );
}
